import { create } from 'zustand';
import { getOrganizationSchedule } from '../services/routeSchedule.service';
import type { TruckWeekSchedule } from '../types/routeSchedule.type';

/**
 * Fleet-wide (multi-truck) read-only week schedule store, backing
 * `FleetScheduleMatrixTab.tsx`. Deliberately a separate store from
 * `routeSchedule.store.ts` -- that one is single-truck and editable-shaped
 * (per-day pending/error state, `setDay`/`clearDay`); this one just holds
 * the whole org's `TruckWeekSchedule[]` as fetched, with no per-row
 * mutation surface.
 */
interface OrganizationScheduleState {
  schedules: TruckWeekSchedule[];
  isLoading: boolean;
  error: string | null;
  clearError: () => void;
  fetchOrganizationSchedule: (organizationId: string) => Promise<TruckWeekSchedule[] | null>;
}

// Same local convention independently duplicated across `routeSchedule.store.ts`,
// `route.store.ts`, and `transportation.store.ts` -- kept local here too rather
// than extracted, to match.
function errorMessage(error: unknown, fallback: string): string {
  const candidate = error as { response?: { data?: { detail?: string | { message?: string } } } };
  const detail = candidate.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail === 'object' && detail.message) return detail.message;
  return fallback;
}

export const useOrganizationScheduleStore = create<OrganizationScheduleState>((set) => ({
  schedules: [],
  isLoading: false,
  error: null,
  clearError: () => set({ error: null }),

  fetchOrganizationSchedule: async (organizationId) => {
    set({ isLoading: true, error: null });
    try {
      const { schedules } = await getOrganizationSchedule(organizationId);
      set({ schedules, isLoading: false });
      return schedules;
    } catch (error) {
      set({ isLoading: false, error: errorMessage(error, 'Failed to load the fleet schedule') });
      return null;
    }
  },
}));
